import React, { useEffect, useMemo, useState, useRef, createElement } from "react";
import { Modal, Input, DatePicker, Select, Checkbox, ConfigProvider, Form, Button, message, Cascader, Tooltip, Tag } from 'antd'
import { CloseSvg, DragSvg, RemoveSvg, CloudSvg } from "@/components/svgs"
import {
    createControlNodeLineAPI, updateControlNodeLineAPI, getControlNodeLineDetailAPI, deleteControlNodeLineAPI,
    getPlmProjectAllTeamUserApi, getControlTemplateNodesAPI, updateControlLineSortAPI,
    updateProjectPaymentExpenseLinks
} from "@/services"
import { useRequest } from "ahooks"
import dayjs from "dayjs";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getNodeStatusColor, k_renderLineType } from "./util"
import type { Option, PayLineData, DefaultNode, ExpenseLineData, ExpenseNode } from "./util"
import clsx from "clsx";
import { documentUtil, toHsl, parseTime, generateRandomColor } from "@/utils"

interface IProps {
    visible: boolean;
    onClose: () => void;
    onUpdate: () => void;
    list: ExpenseLineData[]
    paymentLines?: PayLineData[]
    defaultID?: number | string | null
    forbidEdit?: boolean
}

interface CascaderOption {
    value: string;
    label: React.ReactNode;
    type?: string;
    children?: CascaderOption[];
}
const Index: React.FC<IProps> = (props) => {
    const [modalApi, modalContextHolder] = Modal.useModal();
    const [messageApi, contextHolder] = message.useMessage();
    const params = useParams()
    const projectID = params.projectID || ""

    const { loading: isUpdating, run: updateLinks } = useRequest(updateProjectPaymentExpenseLinks, {
        manual: true,
        onSuccess: () => {
            props.onClose()
            props.onUpdate()
        },
        onError(msg) {
            messageApi.error(msg as any || "更新项目关联节点失败~")
        },
    })
    
    const [currentNodeLine, setCurrentNodeLine] = useState<ExpenseLineData | null>(null)
    const [originalList, setOriginalList] = useState<ExpenseLineData[]>([])
    
    const onChangeTab = (item: ExpenseLineData) => {
        const latestItem = renderList.find(v => v.purContractCode === item.purContractCode)
        setCurrentNodeLine(latestItem ? { ...latestItem } : { ...item })
    }
    
    useEffect(() => {
        if (!props.visible) return
        const listCopy = props.list.map(item => ({
            ...item,
            nodes: item.nodes?.map(node => ({
                ...node,
                links: node.links ? [...node.links] : undefined
            }))
        }))
        setRenderList(listCopy)
        setOriginalList(listCopy)
        if (props.list.length > 0) {
            let item = listCopy[0]
            if (props.defaultID) {
                const defaultItem = listCopy.find(v => v.paymentplancode === props.defaultID)
                if(defaultItem) item = defaultItem
            }
            onChangeTab(item)
        }
    }, [props.visible, props.list])
    
    useEffect(() => {
        if (currentNodeLine && renderList.length > 0) {
            setRenderList(prev => prev.map(item => 
                item.purContractCode === currentNodeLine.purContractCode 
                    ? currentNodeLine 
                    : item
            ))
        }
    }, [currentNodeLine])
    const [formList, setFormList] = useState<Request.PaymentExpenseLink[]>([])
    const onFormChange = (ids: API.PROJECT.PaymentExpenseLink[], index: number) => {
        console.log(ids, index)
        if(!currentNodeLine || !currentNodeLine.nodes) return
        const nodes = currentNodeLine.nodes
        const newNodes: ExpenseNode[] = nodes.map((node, i) => {
            if (index === i) {
                return {
                    ...node,
                    links: ids
                }
            }
            return node
        })
        setCurrentNodeLine({ ...currentNodeLine, nodes: newNodes })
    }
    const linkNodeOptions = useMemo(() => {
        let list = props.list || []
        
        let newlist: CascaderOption[] = []
        if (props.paymentLines && props.paymentLines.length > 0) {
            const paymentLines = props.paymentLines.map(item => {
                return {
                    label: <div className="break-all max-w-[260px] whitespace-pre-wrap">收款线-${item.contractName}</div>,
                    value: item.contractCode,
                    type: k_renderLineType.payment,
                    children: item.nodes.map(v => {
                        return {
                            label: <div className="break-all max-w-[260px] whitespace-pre-wrap">{v.description}</div>,
                            value: v.pmCode,
                            type: k_renderLineType.payment,
                        }
                    })
                }
            })
            newlist.push(...paymentLines)
        }
        return newlist

    }, [currentNodeLine, props.list, props.paymentLines])

    const [renderList, setRenderList] = useState<ExpenseLineData[]>([])
    const onSaveLinks = () => {
        if (isUpdating) return
        const params: Request.PaymentExpenseLink[] = []
        renderList.forEach(line => {
            if (line.nodes) {
                line.nodes.forEach(v => {
                    if (v.links) {
                        v.links.forEach(link => {
                            params.push({
                                fromCode: v.paymentplancode,
                                fromContractCode: line.purContractCode,
                                toCode: link.toCode,
                                toContractCode: link.toContractCode,
                            })
                        })
                    }
                })
            }
        })
        updateLinks(projectID, params)
    }
    const onCloseModal = () => {
        setRenderList(originalList)
        setCurrentNodeLine(null)
        setFormList([])
        props.onClose()
    }
    const getLinkNodeLabel = (linkNode: API.PROJECT.PaymentExpenseLink) => {
        const line = linkNodeOptions.find(v => v.value === linkNode.toContractCode)
        const subNodes = line?.children?.find(n => n.value === linkNode.toCode)
        if (!line || !subNodes) return ""
        return line.label + " - " + subNodes.label
    }
    return (
        <ConfigProvider
            theme={{
                token: {
                    /* 这里是你的全局 token */
                    fontSize: 12
                },
            }}
        >
            {modalContextHolder}
            {contextHolder}
            <Modal open={props.visible} footer={null} maskClosable={false} destroyOnHidden onCancel={onCloseModal} width="fit-content" style={{minWidth: 520}}>
                <div>
                    <div className="border-0 border-b border-solid border-gray-200 overflow-x-auto overflow-y-hidden pb-3 py-2 w-[calc(100%-20px)]">
                        <div className="flex gap-1 pr-4">
                            {(renderList).map((item, index) => {
                                return (
                                    <React.Fragment key={`${item.purContractCode}`}>
                                        <div onClick={() => onChangeTab(item)} key={index}
                                            className={clsx("relative group text-xs px-4 py-2 flex items-center whitespace-nowrap cursor-pointer rounded-sm transition-all duration-200",
                                                currentNodeLine?.purContractCode === item.purContractCode ? "bg-dark-bg text-white hover:bg-dark-bg" : "bg-white text-gray-800 hover:bg-gray-100")}
                                        >
                                            {item.purContractName}
                                        </div>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </div>
                    <div>
                        <div className="mt-2">
                            <table className="text-xs w-full text-center">
                                <tbody>
                                    <tr>
                                        <th className="p-1 whitespace-nowrap">合同编号</th>
                                        <th className="p-1 whitespace-nowrap">时间</th>
                                        <th className="p-1 whitespace-nowrap">供应商名称</th>
                                        <th className="p-1 whitespace-nowrap">付费物料</th>
                                        <th className="p-1 whitespace-nowrap">付款条件</th>
                                        <th className="p-1 min-w-[200px]">关联节点</th>
                                    </tr>
                                    {currentNodeLine?.nodes?.map((item, index) => (
                                        <tr key={index} className="mt-1">
                                            <td className="p-1">
                                                {item.purContractCode}
                                            </td>
                                            <td className="p-1">
                                                {item.date}
                                            </td>
                                            <td className="p-1">
                                                {item.supplierName}
                                            </td>
                                            <td className="p-1">
                                                {item.purchasematter}
                                            </td>
                                            <td className="p-1">
                                                {item.paymentcondition}
                                            </td>
                                            <td className="p-1 min-w-[200px]">
                                                {props.forbidEdit ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.links?.map(v => (
                                                            <Tag>{getLinkNodeLabel(v)}</Tag>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <Cascader 
                                                    className="w-full"
                                                    showCheckedStrategy="SHOW_CHILD"
                                                    value={item.links?.map(v => [v.toContractCode, v.toCode])}
                                                    onChange={(vals, opts) => {
                                                        let paramsIDs: API.PROJECT.PaymentExpenseLink[] = []
                                                        opts.forEach(v => {
                                                            if (v.length > 1) {
                                                                paramsIDs.push({
                                                                    ID: 0,
                                                                    fromContractCode: item.purContractCode,
                                                                    fromCode: item.paymentplancode,
                                                                    toContractCode: v[0].value, 
                                                                    toCode: v[1].value,
                                                                })
                                                            }
                                                        })
                                                        onFormChange(paramsIDs, index)
                                                    }} multiple placeholder="选择节点" options={linkNodeOptions} />
                                                )}
                                                
                                                
                                            </td>
                                            
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {props.forbidEdit ? null : (
                    <div className="flex justify-end mt-4 items-center select-none">
                        <div className="flex">
                            <Button onClick={props.onClose}>取消</Button>
                            <Button className="ml-2" type="primary" onClick={onSaveLinks} loading={isUpdating}>保存</Button>
                        </div>
                        
                    </div>
                )}
            </Modal>
        </ConfigProvider>
    )
}
export default Index